import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import cn from "classnames";
import { CSS_MQ } from "../../../../styles/breakpoints";
import React from "react";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { GuidedSellingStepIconChoice, NotFinalGuidedSellingStep } from "@swo/storefront";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { fontSize } from "../../../../styles/fonts";

export const VerticalLineChoice: ReactTFC<{
  currentStep: NotFinalGuidedSellingStep;
  currentChoice: string | undefined;
  onMakeChoice: (choiceId: string) => void;
  checkedIdx: number;
  onChoiceHovered: (choice: GuidedSellingStepIconChoice) => void;
  totalChoicesCount: number;
}> = props => {
  const reversedIndex = props.currentStep.choices.length - 1 - props.checkedIdx;

  return (
    <div
      css={css`
        display: grid;
        //height: 100%;
        height: auto;

        align-self: center;
      `}
    >
      <div
        className={cn({ centered: shouldUseCenteredLayout(props.currentStep.choices) })}
        css={css`
          padding: 4px;

          display: flex;
          flex-direction: column-reverse;
          justify-content: center;
        `}
      >
        {[...props.currentStep.choices].reverse().map((choice, idx, ctx) => {
          const isChecked = choice.id === props.currentChoice;

          return (
            <div
              onClick={() => props.onMakeChoice(choice.id)}
              className={cn({
                last: idx === ctx.length - 1,
                first: idx === 0,
                selected: isChecked,
                white: reversedIndex >= idx,
                "colored-bar": reversedIndex > idx,
                "higher-bars": props.currentStep.choices.length <= 3,
              })}
              onMouseEnter={() => props.onChoiceHovered(choice)}
              css={[
                wrapper,
                css`
                  -webkit-tap-highlight-color: transparent;

                  --p: 4px;
                  ${CSS_MQ.atLeastSm} {
                    --p: 12px;
                  }
                  ${CSS_MQ.atLeastMd} {
                    --p: 40px;
                  }

                  &.last {
                    padding-top: var(--p);
                  }
                  &.first {
                    padding-bottom: var(--p);
                  }
                `,
              ]}
              key={choice.id}
            >
              <div
                className={cn({ "lower-row-height": props.totalChoicesCount >= 5 })}
                css={[
                  shadowStyles,
                  css`
                    width: 4px;

                    --m: 1;
                    .higher-bars & {
                      --m: 4/3;
                    }

                    --row-height: 40px;
                    @media only screen and (min-height: 700px) {
                      --row-height: 60px;
                    }
                    @media only screen and (min-height: 900px) {
                      --row-height: 80px;
                    }

                    &.lower-row-height {
                      --row-height: 30px;
                      @media only screen and (min-height: 700px) {
                        --row-height: 40px;
                      }
                      @media only screen and (min-height: 900px) {
                        --row-height: 50px;
                      }
                    }

                    --h: calc(var(--m) * var(--row-height));

                    height: var(--h);

                    grid-area: bar;

                    background-color: ${CSS_COLORS.Grey5};
                    .colored-bar & {
                      background-color: ${CSS_COLORS.White};
                    }

                    justify-self: center;

                    .last & {
                      display: none;
                    }
                  `,
                ]}
              />
              <span
                css={[
                  shadowStyles,
                  circleStyles,
                  css`
                    .selected & {
                      background-color: ${CSS_COLORS.White};
                    }
                  `,
                ]}
              />
              <span
                css={[
                  fontSize.x2,
                  css`
                    grid-area: text;

                    padding-top: 2px;
                    padding-left: 4px;
                    color: var(--d);

                    align-self: flex-end;
                  `,
                ]}
              >
                {choice.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const circleStyles = css`
  width: 20px;
  height: 20px;

  display: block;
  border-radius: 100%;

  border: 3px solid var(--c);

  position: relative;

  grid-area: circle;

  justify-self: center;

  &:after {
    content: "";
    position: absolute;
    left: -4px;
    top: -4px;

    width: calc(100% + 4px);
    height: calc(100% + 4px);

    border-radius: 100%;
    border: 2px solid var(--c);
  }
`;

const wrapper = css`
  display: grid;

  grid-template-columns: 30px 1fr;
  grid-template-rows: auto auto;

  grid-template-areas:
    "bar text"
    "circle text";

  .centered & {
    grid-template-columns: 1fr 30px 1fr;
    grid-template-areas:
      "_ bar text"
      "_ circle text";
  }

  justify-self: center;
  cursor: pointer;

  --c: #ccc;
  --d: #ddd;
  &.selected,
  &.white {
    --c: #fff;
  }

  &.selected {
    --d: #fff;
  }

  color: var(--c);
`;

const shadowStyles = css`
  box-shadow: 0px 0px 8px 0px #0000005c;
`;

function shouldUseCenteredLayout(choices: NonEmptyArray<GuidedSellingStepIconChoice>) {
  return choices.every(c => c.label.length <= 30);
}
